// /Users/pramod/Desktop/school-erp/frontend/src/ControllerofExammination/DirectPromotionWorkflow.js

import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';

// ============================================================================
// CONSOLE LOG UTILITY
// ============================================================================

const LOG = {
  info: (message, data = null) => {
    console.log(`%c📘 [INFO] ${message}`, 'color: #2563eb; font-weight: bold;');
    if (data) console.log('  📦 Data:', data);
  },
  success: (message, data = null) => {
    console.log(`%c✅ [SUCCESS] ${message}`, 'color: #16a34a; font-weight: bold;');
    if (data) console.log('  📦 Data:', data);
  },
  warning: (message, data = null) => {
    console.log(`%c⚠️ [WARNING] ${message}`, 'color: #ea580c; font-weight: bold;');
    if (data) console.log('  📦 Data:', data);
  },
  error: (message, data = null) => {
    console.log(`%c❌ [ERROR] ${message}`, 'color: #dc2626; font-weight: bold;');
    if (data) console.log('  📦 Data:', data);
  },
  step: (stepNumber, message, data = null) => {
    console.log(`%c🔷 [STEP ${stepNumber}] ${message}`, 'color: #7c3aed; font-weight: bold; font-size: 14px;');
    if (data) console.log('  📦 Data:', data);
  },
  api: (method, endpoint, data = null) => {
    console.log(`%c🌐 [API] ${method} ${endpoint}`, 'color: #0891b2; font-weight: bold;');
    if (data) console.log('  📤 Payload:', data);
  },
  response: (data) => {
    console.log(`%c📥 [RESPONSE]`, 'color: #059669; font-weight: bold;');
    console.log('  📦 Data:', data);
  },
  state: (stateName, value) => {
    console.log(`%c📌 [STATE] ${stateName}:`, 'color: #d946ef; font-weight: bold;');
    console.log('  📦 Value:', value);
  }
};

// ============================================================================
// UI COMPONENTS
// ============================================================================

const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-12">
    <div className="relative">
      <div className="h-12 w-12 rounded-full border-4 border-gray-200 border-t-gray-700 animate-spin" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="h-6 w-6 rounded-full bg-gray-700 animate-pulse" />
      </div>
    </div>
  </div>
);

const EmptyState = ({ message, icon, subMessage }) => (
  <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white py-16 px-4 text-center">
    <div className="mb-4 text-5xl">{icon || '📋'}</div>
    <p className="text-base font-medium text-gray-700">{message || 'No data available'}</p>
    {subMessage && <p className="mt-1 text-sm text-gray-400">{subMessage}</p>}
  </div>
);

const StatusBadge = ({ status, label, size = 'sm' }) => {
  const colors = {
    PROMOTE: 'bg-green-50 text-green-700 border-green-200',
    PROMOTE_WITH_BACKLOG: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    YEAR_BACK: 'bg-red-50 text-red-700 border-red-200',
    PROVISIONAL: 'bg-purple-50 text-purple-700 border-purple-200',
    PUBLISHED: 'bg-blue-50 text-blue-700 border-blue-200',
    NOT_PUBLISHED: 'bg-gray-100 text-gray-700 border-gray-200',
    archived: 'bg-gray-100 text-gray-700 border-gray-200',
    active: 'bg-green-50 text-green-700 border-green-200'
  };

  const sizeClasses = {
    sm: 'px-2.5 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-1.5 text-base'
  };

  return (
    <span className={`inline-flex items-center rounded-full border font-medium ${sizeClasses[size] || sizeClasses.sm} ${colors[status] || colors.NOT_PUBLISHED}`}>
      <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-current opacity-60" />
      {label || status || 'Unknown'}
    </span>
  );
};

const Card = ({ children, className = '', title, icon, subtitle, actions }) => (
  <div className={`rounded-xl border border-gray-200 bg-white shadow-sm ${className}`}>
    {(title || actions) && (
      <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          {icon && <span className="text-xl">{icon}</span>}
          <div>
            <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
    )}
    <div className="p-5">{children}</div>
  </div>
);

const StepIndicator = ({ currentStep, steps }) => {
  return (
    <div className="relative">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isActive = index + 1 === currentStep;
          const isCompleted = index + 1 < currentStep;

          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div
                  className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold transition-all duration-300 ${
                    isActive
                      ? 'bg-gray-800 text-white shadow-lg shadow-gray-200'
                      : isCompleted
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {isCompleted ? '✓' : step.icon || index + 1}
                </div>
                <span
                  className={`mt-1.5 text-[10px] font-medium transition-colors whitespace-nowrap ${
                    isActive ? 'text-gray-800' : isCompleted ? 'text-green-600' : 'text-gray-400'
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 transition-all duration-300 ${
                    index + 1 < currentStep ? 'bg-green-400' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const Alert = ({ type, message, onDismiss }) => {
  const styles = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800'
  };

  const icons = {
    success: '✅',
    error: '❌',
    warning: '⚠️',
    info: 'ℹ️'
  };

  return (
    <div className={`rounded-lg border p-4 ${styles[type] || styles.info}`}>
      <div className="flex items-start gap-3">
        <span className="text-lg">{icons[type] || '📌'}</span>
        <div className="flex-1 text-sm">{message}</div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="text-current opacity-50 hover:opacity-100 transition-opacity"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};

const Button = ({ children, variant = 'primary', size = 'md', loading, disabled, className = '', ...props }) => {
  const variants = {
    primary: 'bg-gray-800 text-white hover:bg-gray-900 focus:ring-gray-500',
    secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500',
    success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    warning: 'bg-yellow-500 text-white hover:bg-yellow-600 focus:ring-yellow-500',
    purple: 'bg-purple-600 text-white hover:bg-purple-700 focus:ring-purple-500',
    outline: 'border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500'
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-2.5 text-sm'
  };

  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant] || variants.primary} ${sizes[size] || sizes.md} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      )}
      {children}
    </button>
  );
};

const Select = ({ value, onChange, options, placeholder, disabled, loading, getLabel, className = '' }) => (
  <div className="relative">
    <select
      value={value}
      onChange={onChange}
      disabled={disabled || loading}
      className={`w-full appearance-none rounded-lg border border-gray-300 bg-white px-4 py-2.5 pr-10 text-sm text-gray-800 transition-all focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500/20 disabled:bg-gray-50 disabled:text-gray-500 ${className}`}
    >
      <option value="" className="text-gray-400">{placeholder}</option>
      {options.map(opt => (
        <option key={opt._id} value={opt._id} className="text-gray-800 bg-white">
          {getLabel ? getLabel(opt) : opt.name}
        </option>
      ))}
    </select>
    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  </div>
);

// ============================================================================
// CONTEXT SELECTOR COMPONENT
// ============================================================================

const ContextSelector = memo(({ 
  label, 
  tenantId, 
  onContextChange, 
  initialContext,
  isTarget = false 
}) => {
  const [programTypes, setProgramTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [terms, setTerms] = useState([]);
  
  const [programTypeId, setProgramTypeId] = useState(initialContext?.programTypeId || '');
  const [programId, setProgramId] = useState(initialContext?.programId || '');
  const [academicYearId, setAcademicYearId] = useState(initialContext?.academicYearId || '');
  const [semesterId, setSemesterId] = useState(initialContext?.semesterId || '');
  const [termId, setTermId] = useState(initialContext?.termId || '');
  const [isSemesterBased, setIsSemesterBased] = useState(initialContext?.isSemesterBased ?? null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const isFirstMount = useRef(true);
  const prevContextRef = useRef(null);

  // Fetch Program Types
  useEffect(() => {
    if (!tenantId) return;
    LOG.info(`Fetching program types for tenant: ${tenantId}`);
    setLoading(true);
    api.get('/api/program-types', { params: { tenantId, status: 'active' } })
      .then(res => {
        const types = res.data.programTypes || [];
        LOG.success(`Loaded ${types.length} program types`);
        setProgramTypes(types);
      })
      .catch(err => {
        LOG.error('Failed to load program types', err);
        setError('Failed to load program types');
      })
      .finally(() => setLoading(false));
  }, [tenantId]);

  // Fetch Programs
  useEffect(() => {
    if (!tenantId || !programTypeId) {
      setPrograms([]);
      return;
    }
    LOG.info(`Fetching programs for programTypeId: ${programTypeId}`);
    setLoading(true);
    api.get('/api/programs', { params: { tenantId, programTypeId, status: 'active' } })
      .then(res => {
        const progs = res.data.programs || [];
        LOG.success(`Loaded ${progs.length} programs`);
        setPrograms(progs);
      })
      .catch(err => {
        LOG.error('Failed to load programs', err);
        setError('Failed to load programs');
      })
      .finally(() => setLoading(false));
  }, [tenantId, programTypeId]);

  // Fetch Academic Years
  useEffect(() => {
    if (!tenantId) return;
    LOG.info(`Fetching academic years for tenant: ${tenantId}`);
    setLoading(true);
    Promise.all([
      api.get('/api/academic-year/all-active', { params: { tenantId } }),
      api.get('/api/academic-year/all-completed', { params: { tenantId } })
    ])
      .then(([activeRes, completedRes]) => {
        const activeYears = activeRes.data.academicYears || activeRes.data.activeAcademicYears || [];
        const completedYears = completedRes.data.academicYears || [];
        const allYears = [...activeYears, ...completedYears];
        LOG.success(`Loaded ${allYears.length} academic years (${activeYears.length} active, ${completedYears.length} completed)`);
        setAcademicYears(allYears);
      })
      .catch(err => {
        LOG.error('Failed to load academic years', err);
        setError('Failed to load academic years');
      })
      .finally(() => setLoading(false));
  }, [tenantId]);

  // Fetch Semesters/Terms
  useEffect(() => {
    if (!academicYearId) {
      setSemesters([]);
      setTerms([]);
      setIsSemesterBased(null);
      return;
    }

    LOG.info(`Fetching semesters/terms for academicYearId: ${academicYearId}`);
    setLoading(true);
    setSemesters([]);
    setTerms([]);
    setIsSemesterBased(null);
    
    api.get(`/api/classManager/semesters/${academicYearId}`)
      .then(res => {
        const sems = res.data.semesters || [];
        if (sems.length > 0) {
          LOG.success(`Loaded ${sems.length} semesters (Semester-based)`);
          setSemesters(sems);
          setIsSemesterBased(true);
        } else {
          LOG.info('No semesters found, checking for terms...');
          api.get(`/api/academic-year/${academicYearId}/terms`, { params: { tenantId } })
            .then(res2 => {
              const trms = res2.data.terms || [];
              LOG.success(`Loaded ${trms.length} terms (Term-based)`);
              setTerms(trms);
              setIsSemesterBased(false);
            })
            .catch(err => {
              LOG.error('Failed to load terms', err);
              setError('Failed to load terms');
            });
        }
      })
      .catch(() => {
        LOG.info('Semesters API failed, checking for terms...');
        api.get(`/api/academic-year/${academicYearId}/terms`, { params: { tenantId } })
          .then(res2 => {
            const trms = res2.data.terms || [];
            LOG.success(`Loaded ${trms.length} terms (Term-based)`);
            setTerms(trms);
            setIsSemesterBased(false);
          })
          .catch(err => {
            LOG.error('Failed to load semesters/terms', err);
            setError('Failed to load semesters/terms');
          });
      })
      .finally(() => setLoading(false));
  }, [academicYearId, tenantId]);

  // Notify parent of context change
  useEffect(() => {
    if (!onContextChange) return;
    if (!programTypeId || !programId || !academicYearId) return;

    const selectedProgramType = programTypes.find(pt => pt._id === programTypeId);
    const selectedProgram = programs.find(p => p._id === programId);
    const selectedAcademicYear = academicYears.find(y => y._id === academicYearId);
    const selectedSemester = semesters.find(s => s._id === semesterId);
    const selectedTerm = terms.find(t => t._id === termId);

    const context = {
      programTypeId,
      programTypeName: selectedProgramType?.name || '',
      programId,
      programName: selectedProgram?.name || '',
      academicYearId,
      academicYearName: selectedAcademicYear?.name || selectedAcademicYear?.academicYear || '',
      semesterId: isSemesterBased === true ? semesterId : null,
      semesterName: isSemesterBased === true ? (selectedSemester?.name || selectedSemester?.semesterLabel || `Semester ${selectedSemester?.semesterNumber}` || '') : '',
      termId: isSemesterBased === false ? termId : null,
      termName: isSemesterBased === false ? (selectedTerm?.name || selectedTerm?.termLabel || `Term ${selectedTerm?.termNumber}` || '') : '',
      isSemesterBased: isSemesterBased,
      evaluationMode: isSemesterBased === true ? 'sem' : 'term'
    };

    if (isFirstMount.current) {
      isFirstMount.current = false;
      prevContextRef.current = context;
      return;
    }

    const prevContext = prevContextRef.current;
    const contextChanged = JSON.stringify(prevContext) !== JSON.stringify(context);
    
    if (contextChanged) {
      LOG.info(`Context ${isTarget ? 'target' : 'source'} changed:`, context);
      prevContextRef.current = context;
      onContextChange(context);
    }
  }, [programTypeId, programId, academicYearId, semesterId, termId, isSemesterBased, onContextChange, isTarget, programTypes, programs, academicYears, semesters, terms]);

  const getAcademicYearLabel = (ay) => {
    if (!ay) return '';
    const isCompleted = ay?.status === 'completed' || ay?.isCompleted === true;
    return `${ay.name || ay.academicYear}${isCompleted ? ' ✓ (Completed)' : ''}`;
  };

  const getSemesterLabel = (sem) => {
    if (!sem) return '';
    const isCompleted = sem?.status === 'completed' || sem?.isCompleted === true;
    return `${sem.name || sem.semesterLabel || `Semester ${sem.semesterNumber}`}${isCompleted ? ' ✓ (Completed)' : ''}`;
  };

  const getTermLabel = (term) => {
    if (!term) return '';
    const isCompleted = term?.status === 'completed' || term?.isCompleted === true;
    return `${term.name || term.termLabel || `Term ${term.termNumber}`}${isCompleted ? ' ✓ (Completed)' : ''}`;
  };

  return (
    <Card
      title={label || (isTarget ? 'Target Context' : 'Source Context')}
      subtitle={isTarget ? 'Select destination context' : 'Select current academic context'}
      icon={isTarget ? '🎯' : '📂'}
    >
      {error && (
        <Alert type="error" message={error} onDismiss={() => setError('')} />
      )}

      <div className="space-y-4">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-600">Program Type</label>
          <Select
            value={programTypeId}
            onChange={(e) => {
              LOG.info(`Program Type selected: ${e.target.value}`);
              setProgramTypeId(e.target.value);
              setProgramId('');
              setAcademicYearId('');
              setSemesterId('');
              setTermId('');
              setIsSemesterBased(null);
            }}
            options={programTypes}
            placeholder="Select Program Type"
            disabled={!tenantId}
            loading={loading}
          />
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-600">Program</label>
          <Select
            value={programId}
            onChange={(e) => {
              LOG.info(`Program selected: ${e.target.value}`);
              setProgramId(e.target.value);
              setAcademicYearId('');
              setSemesterId('');
              setTermId('');
              setIsSemesterBased(null);
            }}
            options={programs}
            placeholder="Select Program"
            disabled={!programTypeId}
            loading={loading}
          />
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-600">Academic Year</label>
          <Select
            value={academicYearId}
            onChange={(e) => {
              LOG.info(`Academic Year selected: ${e.target.value}`);
              setAcademicYearId(e.target.value);
              setSemesterId('');
              setTermId('');
              setIsSemesterBased(null);
            }}
            options={academicYears}
            placeholder="Select Academic Year"
            disabled={!programId}
            loading={loading}
            getLabel={getAcademicYearLabel}
          />
        </div>

        {isSemesterBased === true && (
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-600">Semester</label>
            <Select
              value={semesterId}
              onChange={(e) => {
                LOG.info(`Semester selected: ${e.target.value}`);
                setSemesterId(e.target.value);
              }}
              options={semesters}
              placeholder="Select Semester"
              disabled={!academicYearId || semesters.length === 0}
              loading={loading}
              getLabel={getSemesterLabel}
            />
          </div>
        )}

        {isSemesterBased === false && (
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-600">Term</label>
            <Select
              value={termId}
              onChange={(e) => {
                LOG.info(`Term selected: ${e.target.value}`);
                setTermId(e.target.value);
              }}
              options={terms}
              placeholder="Select Term"
              disabled={!academicYearId || terms.length === 0}
              loading={loading}
              getLabel={getTermLabel}
            />
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-600 border-t-transparent" />
            Loading...
          </div>
        )}
      </div>
    </Card>
  );
});

ContextSelector.displayName = 'ContextSelector';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const DirectPromotionWorkflow = () => {
  const location = useLocation();
  const navigate = useNavigate();

  LOG.info('🚀 DirectPromotionWorkflow component mounted');

  const { 
    tenantId: propTenantId, 
    user: propUser
  } = location.state || {};

  const [tenantId] = useState(propTenantId || localStorage.getItem('tenantId'));
  const [user] = useState(() => {
    if (propUser) return propUser;
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) return JSON.parse(userStr);
    } catch (e) {
      LOG.error('Failed to parse user from localStorage', e);
    }
    return {};
  });

  const getValidUserId = useCallback(() => {
    const userId = user?._id || user?.id || localStorage.getItem('userId');
    if (userId && /^[0-9a-fA-F]{24}$/.test(userId)) {
      return userId;
    }
    LOG.warning('Invalid user ID found');
    return null;
  }, [user]);

  LOG.info(`📋 Tenant ID: ${tenantId}`);
  LOG.info(`👤 User: ${user?.name || 'Unknown'}`);

  // ============================================================================
  // STATE
  // ============================================================================

  const [sourceContext, setSourceContext] = useState(null);
  const [targetContext, setTargetContext] = useState(null);
  const [step, setStep] = useState(1);
  const [students, setStudents] = useState([]);
  const [, setSelectedStudents] = useState([]);
  const [promotionBatch, setPromotionBatch] = useState(null);
  const [publishedBatch, setPublishedBatch] = useState(null);
  const [, setPromotionResults] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [, setStudentsLoaded] = useState(false);
  const [manualBatchId, setManualBatchId] = useState('');
  const [showPublishOption, setShowPublishOption] = useState(false);
  const [showPreviewTable, setShowPreviewTable] = useState(false);
  const [phase1PreviewData, setPhase1PreviewData] = useState(null);

  // ============================================================================
  // FETCH POLICIES
  // ============================================================================

// Add this before the useEffect that uses fetchPolicies
const fetchPolicies = useCallback(async () => {
  try {
    LOG.api('GET', '/api/coe/student-promotion/available-policies');
    const res = await api.get('/api/coe/student-promotion/available-policies', {
      params: {
        tenantId,
        programId: sourceContext?.programId,
        programTypeId: sourceContext?.programTypeId
      }
    });
    LOG.response(res.data);
    const policyList = res.data.data || [];
    setPolicies(policyList);
    if (policyList.length > 0) {
      LOG.success(`Loaded ${policyList.length} policies, selecting first: ${policyList[0].policyName}`);
      setSelectedPolicy(policyList[0]._id);
    } else {
      LOG.warning('No policies found');
    }
  } catch (err) {
    LOG.error('Failed to load promotion policies', err);
    setError('Failed to load promotion policies');
  }
}, [tenantId, sourceContext?.programId, sourceContext?.programTypeId]);

useEffect(() => {
  if (tenantId && sourceContext?.programId) {
    LOG.info('📜 Fetching promotion policies...');
    fetchPolicies();
  }
}, [tenantId, sourceContext?.programId, fetchPolicies]);

  // ============================================================================
  // RESET FUNCTION
  // ============================================================================

  const resetWorkflow = useCallback(() => {
    LOG.info('🔄 Resetting workflow to step 1');
    setStep(1);
    setStudents([]);
    setSelectedStudents([]);
    setStudentsLoaded(false);
    setPromotionBatch(null);
    setPublishedBatch(null);
    setPreviewData(null);
    setPromotionResults(null);
    setPhase1PreviewData(null);
    setSuccess('');
    setError('');
    setManualBatchId('');
    setShowPublishOption(false);
    setShowPreviewTable(false);
    setSourceContext(null);
    setTargetContext(null);
    setSelectedPolicy('');
  }, []);

  // ============================================================================
  // STEP 1: LOAD STUDENTS
  // ============================================================================

  const loadStudents = useCallback(async () => {
    const context = sourceContext;
    if (!context) {
      LOG.warning('loadStudents called without source context');
      setError('Please select source context first');
      return;
    }

    LOG.step(1, 'Loading students with auto-detect', { sourceContext: context });

    setLoading(true);
    setError('');
    setStudentsLoaded(false);

    try {
      const params = {
        tenantId,
        programId: context.programId,
        programTypeId: context.programTypeId,
        academicYearId: context.academicYearId,
        mode: 'auto'
      };

      if (context.isSemesterBased) {
        params.semesterId = context.semesterId;
      } else {
        params.termId = context.termId;
      }

      LOG.api('GET', '/api/coe/student-promotion/students-with-marks', params);
      const res = await api.get('/api/coe/student-promotion/students-with-marks', { params });
      LOG.response(res.data);
      
      if (res.data.success) {
        const fetchedStudents = res.data.data.students || [];
        let batchId = res.data.data.batchId || null;
        const summary = res.data.data.summary || {};
        
        LOG.success(`Loaded ${fetchedStudents.length} students (mode: ${summary.mode || 'unknown'})`);
        setStudents(fetchedStudents);
        setSelectedStudents(fetchedStudents);
        setStudentsLoaded(true);
        setShowPreviewTable(false);
        
        // ============================================================
        // 🔥 DETECT STATE
        // ============================================================
        const allActive = fetchedStudents.every(s => s.mappingStatus === 'active');
        const allArchived = fetchedStudents.every(s => s.mappingStatus === 'archived');
        const hasProvisional = fetchedStudents.some(s => s.hasProvisionalResult === true);
        const hasPublishedResults = fetchedStudents.some(s => s.resultsStatus === 'PUBLISHED');
        
        LOG.info(`allActive: ${allActive}, allArchived: ${allArchived}, hasProvisional: ${hasProvisional}, hasPublishedResults: ${hasPublishedResults}`);
        LOG.info(`batchId: ${batchId}`);
        
        // ============================================================
        // CASE 1: Results already PUBLISHED → Complete (Step 5)
        // ============================================================
        if (hasPublishedResults && fetchedStudents.length > 0) {
          LOG.info('✅ Results already published - showing complete screen');
          const promoted = fetchedStudents.filter(s => s.decision === 'PROMOTE').length;
          const promotedWithBacklog = fetchedStudents.filter(s => s.decision === 'PROMOTE_WITH_BACKLOG').length;
          const yearBack = fetchedStudents.filter(s => s.decision === 'YEAR_BACK').length;
          
          setPublishedBatch({
            batchId: batchId || 'Published',
            totalStudents: fetchedStudents.length,
            summary: { promoted, promotedWithBacklog, yearBack },
            backlogsCreated: 0
          });
          setStep(5);
          setSuccess(`✅ Results already published! ${fetchedStudents.length} students processed.`);
          return;
        }
        
        // ============================================================
        // CASE 2: Provisionally promoted → Phase 2 (Apply Results)
        // ============================================================
        if (hasProvisional && fetchedStudents.length > 0) {
          LOG.info(`📘 Students already provisionally promoted - ${fetchedStudents.length} students`);
          
          if (!batchId && fetchedStudents.length > 0) {
            const studentWithBatch = fetchedStudents.find(s => s.promotionBatchId);
            if (studentWithBatch) {
              batchId = studentWithBatch.promotionBatchId;
              LOG.success(`Found batch ID from student: ${batchId}`);
            }
          }
          
          setPromotionBatch({ 
            batchId: batchId,
            totalStudents: fetchedStudents.length,
            isExisting: true,
            hasResults: true
          });
          setStep(3);
          
          if (batchId) {
            setSuccess(`📘 Students are provisionally promoted (${fetchedStudents.length} students). Batch ID: ${batchId}. Select a policy and click "Apply Results" to finalize.`);
          } else {
            setSuccess(`📘 Students are provisionally promoted (${fetchedStudents.length} students). Please enter the batch ID manually below.`);
          }
          return;
        }
        
        // ============================================================
        // CASE 3: Active students → Phase 1 (Direct Promote)
        // ============================================================
        if (fetchedStudents.length > 0) {
          LOG.info(`${fetchedStudents.length} students loaded - Active state`);
          setStep(3);
          setSuccess(`✅ ${fetchedStudents.length} students loaded successfully. Select target context and click "Direct Promote All".`);
          
          // Reset any promotion state
          setPromotionBatch(null);
          setPreviewData(null);
          setPromotionResults(null);
          setShowPublishOption(false);
          return;
        }
        
        // ============================================================
        // CASE 4: No students found
        // ============================================================
        if (fetchedStudents.length === 0) {
          LOG.warning('No students found in this context');
          setError('No students found in this context.');
          setSuccess('💡 Try selecting a different context or check if students are mapped to this academic year/term.');
          setStep(1);
          return;
        }
      }
    } catch (err) {
      LOG.error('Failed to load students', err);
      setError(err.response?.data?.message || 'Failed to load students');
    } finally {
      setLoading(false);
      LOG.info('loadStudents completed');
    }
  }, [sourceContext, tenantId]);

  // ============================================================================
  // STEP 2: PREVIEW PROMOTION (PHASE 2 ONLY)
  // ============================================================================

  const previewPromotion = useCallback(async () => {
    const context = sourceContext;
    if (!context || !selectedPolicy) {
      LOG.warning('previewPromotion called without context or policy');
      setError('Please select source context and policy');
      return;
    }

    LOG.step(2, 'Previewing promotion (Phase 2 - Required)', { 
      sourceContext: context, 
      policyId: selectedPolicy 
    });

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        tenantId: tenantId,
        programId: context.programId,
        programTypeId: context.programTypeId,
        academicYearId: context.academicYearId,
        policyId: selectedPolicy,
        includeDirectPromoted: "true"
      };

      if (context.isSemesterBased) {
        payload.semesterId = context.semesterId;
      } else {
        payload.termId = context.termId;
      }

      LOG.api('POST', '/api/coe/student-promotion/promotion-preview', payload);
      
      const res = await api.post('/api/coe/student-promotion/promotion-preview', payload);
      LOG.response(res.data);
      
      if (res.data && res.data.success) {
        LOG.success('Preview completed successfully');
        
        const preview = res.data.data || {};
        const studentsList = preview.students || [];
        const summary = preview.summary || {};
        
        LOG.info(`Preview returned ${studentsList.length} students`);
        LOG.info('Preview summary:', summary);
        
        setPreviewData(preview);
        setStudents(studentsList);
        setSelectedStudents(studentsList);
        setPromotionResults(studentsList);
        setShowPreviewTable(true);
        
        let message = `✅ Preview completed! ${summary.totalStudents || 0} students evaluated.`;
        if (summary.promoted) message += ` ${summary.promoted} promoted,`;
        if (summary.promotedWithBacklog) message += ` ${summary.promotedWithBacklog} with backlog,`;
        if (summary.yearBack) message += ` ${summary.yearBack} year back.`;
        
        setSuccess(message);
        
        LOG.state('previewData', preview);
        LOG.state('students', studentsList);
        LOG.state('showPreviewTable', true);
      } else {
        setError(res.data?.message || 'Preview failed - no data returned');
      }
    } catch (err) {
      LOG.error('Failed to preview promotion', err);
      console.error('Full error:', err);
      
      const errorMessage = err.response?.data?.message || 
                          err.message || 
                          'Failed to preview promotion. Please check the console for details.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [sourceContext, selectedPolicy, tenantId]);

  // ============================================================================
  // STEP 2.5: PREVIEW PROMOTION (PHASE 1 - Before publishing)
  // ============================================================================

  const previewPhase1Promotion = useCallback(async () => {
    const context = sourceContext;
    if (!context || !selectedPolicy) {
      LOG.warning('previewPhase1Promotion called without context or policy');
      setError('Please select source context and policy');
      return;
    }

    if (!promotionBatch?.batchId) {
      LOG.warning('previewPhase1Promotion called without promotion batch');
      setError('No promotion batch found');
      return;
    }

    LOG.step(2.5, 'Previewing promotion (Phase 1 - Before publishing)', { 
      sourceContext: context, 
      policyId: selectedPolicy,
      batchId: promotionBatch.batchId
    });

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        tenantId: tenantId,
        programId: context.programId,
        programTypeId: context.programTypeId,
        academicYearId: context.academicYearId,
        policyId: selectedPolicy,
        includeDirectPromoted: "true"
      };

      if (context.isSemesterBased) {
        payload.semesterId = context.semesterId;
      } else {
        payload.termId = context.termId;
      }

      LOG.api('POST', '/api/coe/student-promotion/promotion-preview', payload);
      
      const res = await api.post('/api/coe/student-promotion/promotion-preview', payload);
      LOG.response(res.data);
      
      if (res.data && res.data.success) {
        LOG.success('Preview completed successfully');
        
        const preview = res.data.data || {};
        const studentsList = preview.students || [];
        const summary = preview.summary || {};
        
        LOG.info(`Preview returned ${studentsList.length} students`);
        LOG.info('Preview summary:', summary);
        
        setPhase1PreviewData(preview);
        setStudents(studentsList);
        setSelectedStudents(studentsList);
        setPromotionResults(studentsList);
        setShowPreviewTable(true);
        
        let message = `✅ Preview completed! ${summary.totalStudents || 0} students will be evaluated.`;
        if (summary.promoted) message += ` ${summary.promoted} promoted,`;
        if (summary.promotedWithBacklog) message += ` ${summary.promotedWithBacklog} with backlog,`;
        if (summary.yearBack) message += ` ${summary.yearBack} year back.`;
        
        setSuccess(message);
        
        LOG.state('phase1PreviewData', preview);
      } else {
        setError(res.data?.message || 'Preview failed - no data returned');
      }
    } catch (err) {
      LOG.error('Failed to preview promotion', err);
      console.error('Full error:', err);
      
      const errorMessage = err.response?.data?.message || 
                          err.message || 
                          'Failed to preview promotion. Please check the console for details.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [sourceContext, selectedPolicy, tenantId, promotionBatch]);

  // ============================================================================
  // STEP 3: DIRECT PROMOTE ALL (PHASE 1 - NO POLICY NEEDED)
  // ============================================================================

  const directPromoteAll = useCallback(async () => {
    const source = sourceContext;
    const target = targetContext;

    if (promotionBatch?.isExisting) {
      LOG.warning('Students already promoted. Use "Apply Results" instead.');
      setError('Students are already provisionally promoted. Use the "Apply Results" button below.');
      return;
    }

    if (!source || !target) {
      LOG.warning('directPromoteAll called without source or target context');
      setError('Please select both source and target contexts');
      return;
    }

    const userId = getValidUserId();
    if (!userId) {
      LOG.error('Valid User ID not found');
      setError('User ID not found. Please login again.');
      return;
    }

    LOG.step(3, 'Direct promoting all students (PHASE 1 - NO POLICY NEEDED)', {
      source,
      target,
      userId
    });

    setLoading(true);
    setError('');
    setShowPublishOption(false);
    setPhase1PreviewData(null);
    setShowPreviewTable(false);

    try {
      const payload = {
        tenantId,
        sourceProgramId: source.programId,
        sourceProgramTypeId: source.programTypeId,
        sourceAcademicYearId: source.academicYearId,
        sourceIsSemesterBased: source.isSemesterBased,
        targetProgramId: target.programId,
        targetProgramTypeId: target.programTypeId,
        targetAcademicYearId: target.academicYearId,
        targetIsSemesterBased: target.isSemesterBased,
        promotedBy: userId,
        remarks: `Direct promotion from ${source.academicYearName || 'source'} to ${target.academicYearName || 'target'}`,
        isProvisional: true
      };

      if (source.isSemesterBased) {
        payload.sourceSemesterId = source.semesterId;
      } else {
        payload.sourceTermId = source.termId;
      }

      if (target.isSemesterBased) {
        payload.targetSemesterId = target.semesterId;
      } else {
        payload.targetTermId = target.termId;
      }

      LOG.api('POST', '/api/coe/student-promotion/direct-promote-all', payload);
      const res = await api.post('/api/coe/student-promotion/direct-promote-all', payload);
      LOG.response(res.data);
      
      if (res.data.success) {
        LOG.success(`Successfully promoted ${res.data.data.totalStudents} students provisionally`);
        LOG.info(`Batch ID: ${res.data.data.batchId}`);
        setPromotionBatch(res.data.data);
        setShowPublishOption(true);
        setStep(4);
        setSuccess(`✅ Successfully promoted ${res.data.data.totalStudents} students provisionally. Batch ID: ${res.data.data.batchId}`);
      }
    } catch (err) {
      LOG.error('Failed to promote students', err);
      setError(err.response?.data?.message || 'Failed to promote students');
    } finally {
      setLoading(false);
    }
  }, [sourceContext, targetContext, tenantId, promotionBatch, getValidUserId]);

// ============================================================================
// STEP 4: PUBLISH RESULTS (PHASE 1 - Optional publish after promotion)
// ============================================================================

const publishResultsPhase1 = useCallback(async () => {
  if (!selectedPolicy) {
    LOG.warning('publishResultsPhase1 called without selected policy');
    setError('Please select a promotion policy first');
    return;
  }

  if (!promotionBatch?.batchId) {
    LOG.warning('publishResultsPhase1 called without promotion batch');
    setError('No promotion batch found');
    return;
  }

  const source = sourceContext;
  if (!source) {
    LOG.warning('publishResultsPhase1 called without source context');
    setError('Source context not found');
    return;
  }

  const userId = getValidUserId();
  if (!userId) {
    LOG.error('Valid User ID not found');
    setError('User ID not found. Please login again.');
    return;
  }

  LOG.step(4, 'Publishing results immediately (PHASE 1 - Optional publish)', {
    batchId: promotionBatch.batchId,
    sourceContext: source,
    policyId: selectedPolicy,
    confirmedBy: userId
  });

  setLoading(true);
  setError('');

  try {
    const payload = {
      tenantId,
      batchId: promotionBatch.batchId,
      policyId: selectedPolicy,
      confirmedBy: userId,
      remarks: `Results published immediately after provisional promotion by ${user?.name || 'System'}`,
      
      // ✅ ADD THESE REQUIRED FIELDS
      sourceProgramId: source.programId,
      sourceProgramTypeId: source.programTypeId,
      sourceAcademicYearId: source.academicYearId,
      sourceIsSemesterBased: source.isSemesterBased,
    };

    if (source.isSemesterBased) {
      payload.sourceSemesterId = source.semesterId;
    } else {
      payload.sourceTermId = source.termId;
    }

    LOG.api('POST', '/api/coe/student-promotion/apply-results', payload);
    const res = await api.post('/api/coe/student-promotion/apply-results', payload);
    LOG.response(res.data);
    
    if (res.data.success) {
      LOG.success('Results published successfully');
      LOG.info(`Updated ${res.data.data.updatedCount} students`);
      LOG.info(`Backlogs created: ${res.data.data.backlogsCreated}`);
      LOG.info('Summary:', res.data.data.summary);
      
      setPublishedBatch(res.data.data);
      setShowPublishOption(false);
      setStep(5);
      setSuccess(`✅ Results published successfully! ${res.data.data.updatedCount} students evaluated. Backlogs created: ${res.data.data.backlogsCreated}`);
    }
  } catch (err) {
    LOG.error('Failed to publish results', err);
    setError(err.response?.data?.message || 'Failed to publish results');
  } finally {
    setLoading(false);
  }
}, [promotionBatch, sourceContext, selectedPolicy, tenantId, user, getValidUserId]);

// ============================================================================
// STEP 5: APPLY RESULTS (PHASE 2 - POLICY NEEDED)
// ============================================================================

const applyResults = useCallback(async () => {
  if (!selectedPolicy) {
    LOG.warning('applyResults called without selected policy');
    setError('Please select a promotion policy first');
    return;
  }

  if (!promotionBatch?.batchId) {
    LOG.warning('applyResults called without promotion batch');
    setError('No promotion batch found');
    return;
  }

  const source = sourceContext;
  if (!source) {
    LOG.warning('applyResults called without source context');
    setError('Source context not found');
    return;
  }

  const userId = getValidUserId();
  if (!userId) {
    LOG.error('Valid User ID not found');
    setError('User ID not found. Please login again.');
    return;
  }

  LOG.step(5, 'Applying final results (PHASE 2 - POLICY NEEDED)', {
    batchId: promotionBatch.batchId,
    sourceContext: source,
    policyId: selectedPolicy,
    confirmedBy: userId
  });

  setLoading(true);
  setError('');

  try {
    const payload = {
      tenantId,
      batchId: promotionBatch.batchId,
      policyId: selectedPolicy,
      confirmedBy: userId,
      remarks: `Final results applied by ${user?.name || 'System'}`,
      
      // ✅ ADD THESE REQUIRED FIELDS
      sourceProgramId: source.programId,
      sourceProgramTypeId: source.programTypeId,
      sourceAcademicYearId: source.academicYearId,
      sourceIsSemesterBased: source.isSemesterBased,
    };

    if (source.isSemesterBased) {
      payload.sourceSemesterId = source.semesterId;
    } else {
      payload.sourceTermId = source.termId;
    }

    LOG.api('POST', '/api/coe/student-promotion/apply-results', payload);
    const res = await api.post('/api/coe/student-promotion/apply-results', payload);
    LOG.response(res.data);
    
    if (res.data.success) {
      LOG.success('Results applied successfully');
      LOG.info(`Updated ${res.data.data.updatedCount} students`);
      LOG.info(`Backlogs created: ${res.data.data.backlogsCreated}`);
      LOG.info('Summary:', res.data.data.summary);
      
      setPublishedBatch(res.data.data);
      setShowPublishOption(false);
      setStep(5);
      setSuccess(`✅ Results applied successfully! ${res.data.data.updatedCount} students evaluated. Backlogs created: ${res.data.data.backlogsCreated}`);
    }
  } catch (err) {
    LOG.error('Failed to apply results', err);
    setError(err.response?.data?.message || 'Failed to apply results');
  } finally {
    setLoading(false);
  }
}, [promotionBatch, sourceContext, selectedPolicy, tenantId, user, getValidUserId]);

  // ============================================================================
  // HANDLE MANUAL BATCH ID
  // ============================================================================

  const handleSetManualBatchId = () => {
    if (manualBatchId && manualBatchId.trim()) {
      LOG.info(`Manual batch ID entered: ${manualBatchId}`);
      setPromotionBatch({ 
        batchId: manualBatchId.trim(),
        totalStudents: students.length,
        isExisting: true
      });
      setSuccess(`Batch ID set: ${manualBatchId.trim()}. Select a policy and click "Apply Results" to finalize.`);
    } else {
      setError('Please enter a valid batch ID');
    }
  };

  // ============================================================================
  // STEP 6: VERIFY TARGET
  // ============================================================================

  const verifyTarget = useCallback(async () => {
    const target = targetContext;
    if (!target) {
      LOG.warning('verifyTarget called without target context');
      setError('Target context not selected');
      return;
    }

    LOG.step(6, 'Verifying target context', { target });

    setLoading(true);
    setError('');

    try {
      const params = {
        tenantId,
        programId: target.programId,
        programTypeId: target.programTypeId,
        academicYearId: target.academicYearId,
        mode: 'active'
      };

      if (target.isSemesterBased) {
        params.semesterId = target.semesterId;
      } else {
        params.termId = target.termId;
      }

      LOG.api('GET', '/api/coe/student-promotion/students-with-marks', params);
      const res = await api.get('/api/coe/student-promotion/students-with-marks', { params });
      LOG.response(res.data);
      
      if (res.data.success) {
        const verifiedStudents = res.data.data.students || [];
        LOG.success(`Found ${verifiedStudents.length} students in target context`);
        setStudents(verifiedStudents);
        setSuccess(`✅ ${verifiedStudents.length} students found in target context`);
      }
    } catch (err) {
      LOG.error('Failed to verify target', err);
      setError(err.response?.data?.message || 'Failed to verify target');
    } finally {
      setLoading(false);
    }
  }, [targetContext, tenantId]);

  // ============================================================================
  // BACK BUTTON
  // ============================================================================

  const handleBack = () => {
    LOG.info('Navigating back to promotion options');
    navigate('/coe/promote-students');
  };

  // ============================================================================
  // RENDER FUNCTIONS
  // ============================================================================

  const StudentTable = ({ studentsList, title = 'Students' }) => {
    if (!studentsList || studentsList.length === 0) {
      return <EmptyState message={`No ${title} found`} subMessage="Try adjusting your search criteria" />;
    }

    return (
      <div className="overflow-x-auto -mx-5 sm:mx-0">
        <div className="inline-block min-w-full align-middle">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">USN</th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name</th>
                <th className="hidden px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 md:table-cell">Class</th>
                <th className="px-3 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">Overall %</th>
                <th className="px-3 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">Failed</th>
                <th className="px-3 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-3 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">Decision</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {studentsList.map((student, idx) => (
                <tr key={student.studentId || idx} className="transition-colors hover:bg-gray-50">
                  <td className="whitespace-nowrap px-3 py-3 font-mono text-xs text-gray-600">{student.USN || 'N/A'}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-sm font-medium text-gray-800">{student.name || 'Unknown'}</td>
                  <td className="hidden whitespace-nowrap px-3 py-3 text-sm text-gray-600 md:table-cell">{student.className || 'N/A'}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-center text-sm font-semibold text-gray-800">
                    {student.overallPercentage || student.metrics?.overallPercentage || 0}%
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-center text-sm">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      (student.failedSubjectsCount || student.metrics?.failedSubjectsCount || 0) > 0
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {student.failedSubjectsCount || student.metrics?.failedSubjectsCount || 0}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-center">
                    <StatusBadge 
                      status={student.mappingStatus || 'active'} 
                      label={student.mappingStatus || 'Active'} 
                      size="sm"
                    />
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-center">
                    {student.decision ? (
                      <StatusBadge 
                        status={student.decision} 
                        label={student.decision.replace(/_/g, ' ')} 
                        size="sm"
                      />
                    ) : (
                      <span className="text-xs text-gray-400">Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  const steps = [
    { id: 1, label: 'Context', icon: '📌' },
    { id: 2, label: 'Preview', icon: '👁️' },
    { id: 3, label: 'Promote', icon: '🚀' },
    { id: 4, label: 'Apply', icon: '📊' },
    { id: 5, label: 'Complete', icon: '✅' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <div className="rounded-xl bg-white border border-gray-200 p-6 shadow-sm md:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                aria-label="Go back"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Direct Promotion Workflow</h1>
                <p className="text-sm text-gray-500">Provisionally promote students and apply final results</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {promotionBatch && !publishedBatch && (
                <div className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm text-gray-700">
                  Batch: <span className="font-mono font-semibold">{promotionBatch.batchId || 'Not Set'}</span>
                </div>
              )}
              {publishedBatch && (
                <div className="rounded-lg bg-green-100 px-3 py-1.5 text-sm text-green-700">
                  ✅ Published: <span className="font-mono font-semibold">{publishedBatch.batchId}</span>
                </div>
              )}
            </div>
          </div>

          {/* Step Indicator */}
          <div className="mt-6">
            <StepIndicator currentStep={step} steps={steps} />
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <Alert type="error" message={error} onDismiss={() => setError('')} />
        )}
        {success && (
          <Alert type="success" message={success} onDismiss={() => setSuccess('')} />
        )}

        {/* Loading State */}
        {loading && <LoadingSpinner />}

        {/* ============================================ */}
        {/* STEP 1: CONTEXT SELECTION */}
        {/* ============================================ */}
        {step >= 1 && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <ContextSelector
              tenantId={tenantId}
              label="Source Context"
              initialContext={sourceContext}
              onContextChange={(context) => {
                LOG.info('Source context changed:', context);
                setSourceContext(context);
                setStudentsLoaded(false);
                setPromotionBatch(null);
                setPreviewData(null);
                setPromotionResults(null);
                setPublishedBatch(null);
                setShowPublishOption(false);
                setShowPreviewTable(false);
                setPhase1PreviewData(null);
              }}
            />

            {/* Target Context - ONLY show in Phase 1 (NOT when already promoted) */}
            {step >= 3 && !publishedBatch && !promotionBatch?.isExisting && (
              <ContextSelector
                tenantId={tenantId}
                label="Target Context"
                initialContext={targetContext}
                onContextChange={(context) => {
                  LOG.info('Target context changed:', context);
                  setTargetContext(context);
                }}
                isTarget={true}
              />
            )}
          </div>
        )}

        {/* Load Students Button */}
        {step >= 1 && step < 3 && sourceContext && (
          <div className="flex justify-center">
            <Button
              onClick={() => {
                LOG.info('Load Students button clicked');
                loadStudents();
              }}
              disabled={loading}
              size="lg"
              className="min-w-[200px]"
            >
              <span className="flex items-center gap-2">
                {loading ? 'Loading...' : '📥 Load Students'}
              </span>
            </Button>
          </div>
        )}

        {/* ============================================ */}
        {/* PHASE 1: STUDENTS LIST & DIRECT PROMOTE */}
        {/* ============================================ */}
        {step >= 3 && students.length > 0 && !publishedBatch && !promotionBatch?.isExisting && !showPreviewTable && (
          <Card
            title="Students Found"
            subtitle={`${students.length} student${students.length > 1 ? 's' : ''} in the current context`}
            actions={
              <span className="text-xs text-gray-400">Phase 1: No policy required</span>
            }
          >
            <StudentTable studentsList={students} title="Students" />

            <div className="mt-6 border-t border-gray-200 pt-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-green-700">Phase 1: Direct Promote All</h4>
                  <p className="text-xs text-gray-500">
                    Provisionally promote all {students.length} students to the target context.
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    ℹ️ No policy required for provisional promotion.
                  </p>
                </div>
                <Button
                  onClick={directPromoteAll}
                  disabled={loading || !targetContext}
                  variant="success"
                  size="lg"
                  className="min-w-[180px]"
                >
                  {loading ? 'Promoting...' : '🚀 Direct Promote All'}
                </Button>
              </div>
              {!targetContext && (
                <p className="mt-2 text-xs text-yellow-600">⚠️ Please select target context first</p>
              )}
            </div>
          </Card>
        )}

        {/* ============================================ */}
        {/* PHASE 1: AFTER DIRECT PROMOTE - SUCCESS & PUBLISH OPTION */}
        {/* ============================================ */}
        {step === 4 && promotionBatch && !promotionBatch?.isExisting && !publishedBatch && showPublishOption && (
          <Card
            title="Promotion Successful"
            subtitle={`${promotionBatch.totalStudents} students provisionally promoted`}
            icon="🎉"
            className="border-green-200 bg-green-50/30"
          >
            <div className="space-y-4">
              <div className="rounded-lg bg-white/70 p-4">
                <p className="text-sm font-medium text-gray-800">
                  Batch ID: <span className="font-mono">{promotionBatch.batchId}</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Students are now in the target context. You can publish results now or do it later.
                </p>
              </div>

              <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <label className="mb-1.5 block text-xs font-medium text-gray-600">
                    Select Promotion Policy <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={selectedPolicy}
                    onChange={(e) => {
                      LOG.info(`Policy selected for Phase 1 publish: ${e.target.value}`);
                      setSelectedPolicy(e.target.value);
                      // Clear preview data when policy changes
                      setPhase1PreviewData(null);
                      setShowPreviewTable(false);
                    }}
                    options={policies}
                    placeholder="Select a policy"
                    getLabel={(p) => p.policyName}
                  />
                </div>
                <div className="flex flex-wrap gap-3 self-end">
                  <Button
                    onClick={previewPhase1Promotion}
                    disabled={loading || !selectedPolicy}
                    variant="secondary"
                    size="lg"
                  >
                    👁️ Preview Results
                  </Button>
                  <Button
                    onClick={publishResultsPhase1}
                    disabled={loading || !selectedPolicy}
                    variant="primary"
                    size="lg"
                  >
                    {loading ? 'Publishing...' : '📊 Publish Results Now'}
                  </Button>
                  <Button
                    onClick={() => {
                      LOG.info('Do It Later button clicked - Resetting workflow');
                      resetWorkflow();
                    }}
                    variant="outline"
                  >
                    ⏳ Do It Later
                  </Button>
                </div>
              </div>
              {!selectedPolicy && (
                <p className="text-xs text-yellow-600">⚠️ Please select a policy above before previewing or publishing.</p>
              )}

              {/* Phase 1 Preview Results */}
              {phase1PreviewData && phase1PreviewData.summary && (
                <div className="rounded-lg bg-white/70 p-4 mt-4">
                  <h4 className="mb-3 text-sm font-semibold text-gray-800">Preview Summary</h4>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div className="rounded-lg bg-gray-50 p-3 text-center">
                      <span className="text-xs text-gray-500">Total</span>
                      <p className="text-lg font-bold text-gray-800">{phase1PreviewData.summary?.totalStudents || 0}</p>
                    </div>
                    <div className="rounded-lg bg-green-50 p-3 text-center">
                      <span className="text-xs text-green-600">Promote</span>
                      <p className="text-lg font-bold text-green-700">{phase1PreviewData.summary?.promoted || 0}</p>
                    </div>
                    <div className="rounded-lg bg-yellow-50 p-3 text-center">
                      <span className="text-xs text-yellow-600">With Backlog</span>
                      <p className="text-lg font-bold text-yellow-700">{phase1PreviewData.summary?.promotedWithBacklog || 0}</p>
                    </div>
                    <div className="rounded-lg bg-red-50 p-3 text-center">
                      <span className="text-xs text-red-600">Year Back</span>
                      <p className="text-lg font-bold text-red-700">{phase1PreviewData.summary?.yearBack || 0}</p>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-green-600">✅ Preview complete. Click "Publish Results Now" to finalize.</p>
                </div>
              )}

              {/* Phase 1 Preview Student Table */}
              {showPreviewTable && phase1PreviewData && students.length > 0 && (
                <div className="mt-4">
                  <h4 className="mb-3 text-sm font-semibold text-gray-800">Preview Results - Student Details</h4>
                  <StudentTable studentsList={students} title="Preview Students" />
                </div>
              )}
            </div>
          </Card>
        )}

        {/* ============================================ */}
        {/* PHASE 2: APPLY RESULTS (NO TARGET CONTEXT) */}
        {/* ============================================ */}
        {(step === 3 || step === 4) && promotionBatch?.isExisting && !publishedBatch && (
          <Card
            title="Phase 2: Apply Final Results"
            subtitle="Students are already provisionally promoted. Apply results to finalize."
            icon="📋"
            className="border-purple-200 bg-purple-50/30"
          >
            <div className="space-y-4">
              <Alert type="warning" message="⚠️ Policy is REQUIRED for Phase 2" />

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-600">
                    Select Promotion Policy <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={selectedPolicy}
                    onChange={(e) => {
                      LOG.info(`Policy selected for Phase 2: ${e.target.value}`);
                      setSelectedPolicy(e.target.value);
                    }}
                    options={policies}
                    placeholder="Select a policy"
                    getLabel={(p) => p.policyName}
                  />
                </div>

                {!promotionBatch?.batchId && (
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-gray-600">Batch ID:</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={manualBatchId}
                        onChange={(e) => setManualBatchId(e.target.value)}
                        placeholder="Enter batch ID"
                        className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-800 transition-all focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500/20"
                      />
                      <Button onClick={handleSetManualBatchId} variant="secondary" size="sm">
                        Set
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {promotionBatch?.batchId && (
                <div className="rounded-lg bg-white/70 p-4 text-sm">
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                    <div>
                      <span className="text-xs text-gray-500">Batch ID</span>
                      <p className="font-mono font-semibold text-gray-800">{promotionBatch.batchId}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Students</span>
                      <p className="font-semibold text-gray-800">{promotionBatch.totalStudents || students.length}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Status</span>
                      <StatusBadge status="PROVISIONAL" label="Provisionally Promoted" size="sm" />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={previewPromotion}
                  disabled={loading || !selectedPolicy}
                  variant="secondary"
                  size="lg"
                >
                  👁️ Preview Results
                </Button>
                <Button
                  onClick={applyResults}
                  disabled={loading || !promotionBatch?.batchId || !selectedPolicy || !previewData}
                  variant="primary"
                  size="lg"
                >
                  📊 Apply Results
                </Button>
              </div>

              {promotionBatch?.batchId && !selectedPolicy && (
                <p className="text-xs text-purple-600">⚠️ Please select a policy first</p>
              )}
              {!promotionBatch?.batchId && (
                <p className="text-xs text-purple-600">⚠️ Please enter batch ID above</p>
              )}

              {/* Preview Summary Stats */}
              {previewData && previewData.summary && (
                <div className="rounded-lg bg-white/70 p-4">
                  <h4 className="mb-3 text-sm font-semibold text-gray-800">Preview Summary</h4>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div className="rounded-lg bg-gray-50 p-3 text-center">
                      <span className="text-xs text-gray-500">Total</span>
                      <p className="text-lg font-bold text-gray-800">{previewData.summary?.totalStudents || 0}</p>
                    </div>
                    <div className="rounded-lg bg-green-50 p-3 text-center">
                      <span className="text-xs text-green-600">Promote</span>
                      <p className="text-lg font-bold text-green-700">{previewData.summary?.promoted || 0}</p>
                    </div>
                    <div className="rounded-lg bg-yellow-50 p-3 text-center">
                      <span className="text-xs text-yellow-600">With Backlog</span>
                      <p className="text-lg font-bold text-yellow-700">{previewData.summary?.promotedWithBacklog || 0}</p>
                    </div>
                    <div className="rounded-lg bg-red-50 p-3 text-center">
                      <span className="text-xs text-red-600">Year Back</span>
                      <p className="text-lg font-bold text-red-700">{previewData.summary?.yearBack || 0}</p>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-green-600">✅ Preview complete. Review the student details below and click "Apply Results" to finalize.</p>
                </div>
              )}

              {/* Preview Student Table - Always show when previewData exists */}
              {showPreviewTable && students.length > 0 && previewData && (
                <div className="mt-4">
                  <h4 className="mb-3 text-sm font-semibold text-gray-800">Preview Results - Student Details</h4>
                  <StudentTable studentsList={students} title="Preview Students" />
                </div>
              )}
            </div>
          </Card>
        )}

        {/* ============================================ */}
        {/* STEP 5: COMPLETE */}
        {/* ============================================ */}
        {step === 5 && publishedBatch && (
          <Card
            title="🎉 Promotion Complete!"
            subtitle={`${publishedBatch.totalStudents} students processed successfully`}
            icon="✅"
            className="border-green-200 bg-green-50/30"
          >
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg bg-white/70 p-4 text-center">
                <span className="text-xs text-gray-500">Total Students</span>
                <p className="text-2xl font-bold text-gray-800">{publishedBatch.totalStudents}</p>
              </div>
              <div className="rounded-lg bg-white/70 p-4 text-center">
                <span className="text-xs text-green-600">Promoted</span>
                <p className="text-2xl font-bold text-green-700">{publishedBatch.summary?.promoted || 0}</p>
              </div>
              <div className="rounded-lg bg-white/70 p-4 text-center">
                <span className="text-xs text-yellow-600">With Backlog</span>
                <p className="text-2xl font-bold text-yellow-700">{publishedBatch.summary?.promotedWithBacklog || 0}</p>
              </div>
              <div className="rounded-lg bg-white/70 p-4 text-center">
                <span className="text-xs text-red-600">Year Back</span>
                <p className="text-2xl font-bold text-red-700">{publishedBatch.summary?.yearBack || 0}</p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={verifyTarget} disabled={loading} variant="secondary">
                🔍 Verify Target Context
              </Button>
              <Button
                onClick={() => {
                  LOG.info('Starting new promotion');
                  resetWorkflow();
                }}
                variant="success"
              >
                🔄 Start New Promotion
              </Button>
              <Button onClick={handleBack} variant="outline">
                🏠 Back to Dashboard
              </Button>
            </div>
          </Card>
        )}

        {/* VERIFIED STUDENTS IN TARGET */}
        {step === 5 && students.length > 0 && (
          <Card
            title="Students in Target Context"
            subtitle={`${students.length} student${students.length > 1 ? 's' : ''} found`}
          >
            <StudentTable studentsList={students} title="Target Students" />
          </Card>
        )}

        {/* BATCH BACKLOGS */}
        {publishedBatch && (
          <Card
            title="Backlogs Created"
            subtitle={`${publishedBatch.backlogsCreated || 0} backlog${publishedBatch.backlogsCreated > 1 ? 's' : ''} assigned`}
            icon="📚"
          >
            <p className="text-sm text-gray-600">
              Students with failed subjects have been assigned backlogs.
              {publishedBatch.summary?.yearBack > 0 && (
                <span className="block mt-1 text-red-600">
                  ⚠️ {publishedBatch.summary.yearBack} student{publishedBatch.summary.yearBack > 1 ? 's' : ''} need to repeat the year.
                </span>
              )}
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DirectPromotionWorkflow;